import React, { useState } from "react";

import ChevronDown from "@uui/ChevronDown";
import ChevronUp from "@uui/ChevronUp";
import { isEmpty } from "lodash/fp";

import type { SxProps } from "@mui/material";
import Collapse from "@mui/material/Collapse";

import AlertBanner, { AlertBannerProps } from "@rui/AlertBanner";
import AlertBannerActions from "@rui/AlertBanner/AlertBannerActions";
import { Text } from "@rui/AlertBanner/styles";
import { Column } from "@rui/Stack";
import { Bold } from "@rui/styles";

import { NestedBanner, Preview, ViewToggleButton } from "./styles";

type SingleBannerProps = Omit<
  AlertBannerProps,
  "direction" | "severity" | "onClose"
> & { content: React.ReactNode };

export interface AlertBannerGroupProps {
  alerts: SingleBannerProps[];
  onClose?: AlertBannerProps["onClose"];
  severity: AlertBannerProps["severity"];
  title: string;
}

const ALERT_BANNER_STYLE_OVERRIDES: SxProps = {
  alignItems: "flex-start",
  "& .MuiAlert-message": { flex: 1 },
  "& .MuiAlert-icon": {
    minHeight: "28px",
    alignItems: "center",
  },
};

const AlertBannerGroup = ({
  alerts,
  onClose,
  severity,
  title,
}: AlertBannerGroupProps) => {
  const [viewAll, setViewAll] = useState(false);

  if (isEmpty(alerts)) return null;

  if (alerts.length === 1) {
    const { content, ...alert } = alerts[0];
    return (
      <AlertBanner
        severity={severity}
        title={title}
        onClose={onClose}
        {...alert}
      >
        {content}
      </AlertBanner>
    );
  }

  const handleToggleViewAll = () => setViewAll((va) => !va);

  return (
    <>
      <AlertBanner
        severity={severity}
        onClose={onClose}
        contentVariant="custom"
        sx={ALERT_BANNER_STYLE_OVERRIDES}
      >
        <Column flex={1}>
          <Preview
            variant="left"
            spacing={2}
            onClick={viewAll ? undefined : handleToggleViewAll}
          >
            <Text>
              <Bold>{title}</Bold>
            </Text>
            {!viewAll && (
              <ViewToggleButton endIcon={<ChevronDown />}>
                View
              </ViewToggleButton>
            )}
          </Preview>
        </Column>
        <Collapse in={viewAll} sx={{ width: "100%" }}>
          {alerts.map((alert, i) => (
            <NestedBanner
              key={i}
              action={
                <AlertBannerActions severity={severity} action={alert.action} />
              }
            >
              <Text>
                {alert.title && <Bold>{alert.title}: </Bold>}
                {alert.content}
              </Text>
            </NestedBanner>
          ))}
          <ViewToggleButton
            endIcon={<ChevronUp />}
            onClick={handleToggleViewAll}
          >
            Collapse
          </ViewToggleButton>
        </Collapse>
      </AlertBanner>
    </>
  );
};

export default AlertBannerGroup;
